import React, { useState, useEffect, useCallback } from 'react';
import { ChevronUp } from 'lucide-react';

/**
 * Global BackToTop button.
 * Shows after scrolling ~35% of the page (or 400px).
 * Supports both window scroll (public pages) and custom scroll containers.
 *
 * @param {string} [scrollSelector] - optional CSS selector for a custom scroll container
 */
export default function BackToTop({ scrollSelector }) {
  const [visible, setVisible] = useState(false);

  const getContainer = useCallback(() => {
    if (scrollSelector) return document.querySelector(scrollSelector);
    return null; // window scroll
  }, [scrollSelector]);

  useEffect(() => {
    const container = getContainer();
    const target = container || window;

    const handleScroll = () => {
      if (container) {
        const pct = container.scrollTop / (container.scrollHeight - container.clientHeight);
        setVisible(pct > 0.25 || container.scrollTop > 400);
      } else {
        const pct = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
        setVisible(pct > 0.25 || window.scrollY > 400);
      }
    };

    target.addEventListener('scroll', handleScroll, { passive: true });
    return () => target.removeEventListener('scroll', handleScroll);
  }, [getContainer]);

  const scrollUp = () => {
    const container = getContainer();
    if (container) {
      container.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <button
      onClick={scrollUp}
      data-testid="back-to-top-btn"
      aria-label="Back to top"
      className={`
        md:hidden fixed left-6 z-40 w-12 h-12 rounded-full
        bg-white/90 dark:bg-slate-800/90 hover:bg-white dark:hover:bg-slate-800
        text-slate-700 dark:text-slate-300 shadow-lg hover:shadow-xl
        backdrop-blur-sm border border-slate-200 dark:border-slate-700
        flex items-center justify-center
        transition-all duration-300
        ${visible
          ? 'opacity-100 translate-y-0 pointer-events-auto'
          : 'opacity-0 translate-y-4 pointer-events-none'
        }
      `}
      style={{ bottom: '24px' }}
    >
      <ChevronUp className="w-5 h-5" />
    </button>
  );
}
